import { useTracking } from 'react-tracking';
import { getPath } from '@/router/utils';
import { ROUTE_NARRATIVE_COMMUNITY } from '@/constants/routes';
import { Button } from '@simplywallst/ui-core/next';
import { FairValueHistogram } from '../../FairValueHistogram';
import type { Narrative } from '../hooks/useCompanyNarratives';
import { useCompanyNarratives } from '../hooks/useCompanyNarratives';
import { CompanyNarrativesList } from './CompanyNarrativesList';
import { useMemo } from 'react';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { CompanyNarrativesHeader } from './CompanyNarrativesHeader';
import { useMount } from '@simplywallst/ui-core';
import { useSponsoredNarratives } from '../hooks/useSponsoredNarratives';

type Props = {
  canonicalUrl: string;
};

export const CompanyNarrativesMain = ({ canonicalUrl }: Props) => {
  const { trackEvent } = useTracking();
  const { data } = useCompanyNarratives({ canonicalUrl });
  const { company } = data;
  const { data: sponsoredNarrativesData } = useSponsoredNarratives({
    companyId: company?.id,
  });

  const communityNarrativeCount = data?.narratives.edges.length ?? 0;
  const shouldShowHistogram = company.valuationHistogram.count > 5;
  const narratives = useMemo(() => {
    const { narratives: narrativeConnections, company } = data;
    const sponsoredNarratives =
      sponsoredNarrativesData?.sponsoredNarratives ?? [];
    return [
      company.myValuation?.narrative,
      company.myNarrative,
      ...sponsoredNarratives,
      ...narrativeConnections.edges.map((edge) => edge.node),
    ].reduce((result, narrative) => {
      if (!narrative || result.some((item) => item.id === narrative.id))
        return result;

      return [...result, narrative];
    }, [] as Narrative[]);
  }, [data, sponsoredNarrativesData]);

  useMount(() => {
    trackEvent({
      subject: 'narratives',
      action: 'loaded',
      narratives: narratives.map((narrative) => ({ id: narrative.id })),
    });
  });

  const { country, industry, exchangeTicker, companyName } =
    getCompanyUrlSegments(canonicalUrl);

  return (
    <>
      <CompanyNarrativesHeader
        companyId={company.id}
        uniqueSymbol={company.uniqueSymbol}
      />

      <section className="grid gap-x2">
        <p className="m-0 text-sm text-soft">
          Select a narrative for quick price alerts from the community, or
          create your own.
        </p>
        {shouldShowHistogram && (
          <FairValueHistogram company={company} height={160} />
        )}

        <CompanyNarrativesList
          canonicalUrl={canonicalUrl}
          narratives={narratives}
        />

        <div className="grid">
          {communityNarrativeCount > 0 && (
            <Button
              variant="secondary"
              href={getPath({
                type: ROUTE_NARRATIVE_COMMUNITY,
                payload: {
                  country,
                  industry,
                  companyname: companyName,
                  exchange_ticker: exchangeTicker,
                },
              })}
              onClick={() => {
                trackEvent({
                  action: 'click',
                  modifier: 'view-all',
                });
              }}
            >
              View {company.name} Community
            </Button>
          )}
        </div>
      </section>
    </>
  );
};
